/* eslint-disable max-len */
/* eslint-disable no-alert */
/* eslint-disable no-nested-ternary */
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import rightSide from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/assets/right-side.svg';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { setForm, UploadedFile } from '@brands/store/slices/patientFormSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { clearPreviewCache } from '@brands/Utils/mediaPreviewCache';
import { uploadIntakeImage } from '@brands/Utils/uploadIntakeImage';
import { UploadedFileImage } from '@brands/Components/UploadedFileImage/UploadedFileImage';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { VscDeviceCamera } from 'react-icons/vsc';

import Container from '../../../../Components/Container/Container';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useReduxHook';
import useWindowSize from '../../../../hooks/useWindowSize';
import AddDependentModal from '../../../MyProfile/Patient/Sections/AddDependentModal/AddDependentModal';
import Camera from './Components/Camera';
import styles from './styles.module.scss';

const dataURLToFile = async (dataURL: string, filename: string): Promise<File> => {
  const response = await fetch(dataURL);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

const ImageUploadStepOne = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const roleNavigate = useRoleNavigate();
  const { userInfo } = useAppSelector(selectAuth);
  const { values } = useAppSelector(selectPatientForm);
  const screenSize = useWindowSize();
  const [openCamera, setOpenCamera] = useState(false);
  const [showAddDependentModal, setShowAddDependentModal] = useState<boolean>(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<UploadedFile | null>(
    (values.find((item) => item.form_question_id === 17)?.value.images as UploadedFile[])?.find(
      (leftSideImage) => leftSideImage.name === 'RightSideImage.png',
    ) || null,
  );

  const onCloseCamera = (): void => {
    setOpenCamera(false);
  };

  const onPhotoTaken = async (uri: string): Promise<void> => {
    try {
      setUploading(true);
      const file = await dataURLToFile(uri, 'RightSideImage.png');
      const uploaded = await uploadIntakeImage(file, userInfo?.role?.name);
      setImages({ ...uploaded, name: 'RightSideImage.png' });
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setUploading(false);
    }
  };

  const deleteMedia = (): void => {
    setImages((prev) => {
      if (prev?.media_id) clearPreviewCache(prev.media_id);
      return null;
    });
  };

  useEffect(() => {
    const payload = [...values];
    const filteredPayload = payload.filter((item) => item.form_question_id !== 17);

    if (images === null) {
      const existingImages =
        (values.find((item) => item.form_question_id === 17)?.value.images as UploadedFile[]) || [];

      const updatedImages = existingImages.filter((img) => img.name !== 'RightSideImage.png');

      filteredPayload.push({
        form_question_id: 17,
        value: {
          images: updatedImages,
        },
      });

      dispatch(setForm(filteredPayload));
    } else {
      const existingImages =
        (values.find((item) => item.form_question_id === 17)?.value.images as UploadedFile[]) || [];

      const updatedImages = existingImages.filter((img) => img.name !== images.name);
      updatedImages.push(images);

      filteredPayload.push({
        form_question_id: 17,
        value: {
          images: updatedImages,
        },
      });

      dispatch(setForm(filteredPayload));
    }
  }, [images]);

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
      }}
      childrenStyle={{ zIndex: '2' }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={styles.uploadImageContainer}>
        <div className={styles.uploadImageContainerDiv}>
          <PatientIntakeNavigation progress={72} />
          <div className={`${styles.formTitle} d-flex mt-3`}>Guided Image Capture</div>
          <form className={styles.formContainer}>
            <div className={styles.wrapper}>
              <div className={styles.formContent}>
                <div className={styles.uploadFlow}>
                  <span className={styles.formContentFlowImageSpan}>
                    {images !== null ? (
                      <div className={`${styles.formContentFlowImageSpan}`}>
                        <div className={styles.imageWrap} key={images.media_id ?? images.name}>
                          <span
                            className={styles.removeImage}
                            onClick={() => deleteMedia()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && deleteMedia()}
                          >
                            <IoClose />
                          </span>
                          <UploadedFileImage file={images} userRole={userInfo?.role?.name} />
                        </div>
                      </div>
                    ) : (
                      <img src={rightSide} />
                    )}
                  </span>
                  <span className={styles.uploadImageQRContainerText2}>Image 1 of 5</span>
                  <span className={styles.uploadImageQRContainerText1}>Right Side</span>

                  {images === null && (
                    <span className={styles.uploadImageQRContainerText3}>
                      Turn your head slightly to the left, and use your index and middle fingers of your right hand to
                      pull back your right cheek.
                    </span>
                  )}

                  <div
                    className={`${styles.selectImages} ${styles.cameraBtn}`}
                    onClick={() => !uploading && setOpenCamera(true)}
                  >
                    <VscDeviceCamera />
                    {uploading ? 'Uploading…' : images === null ? 'Take Photo' : 'Retake Photo'}
                  </div>
                </div>
              </div>
              <div className={styles.btnContainer}>
                <Button
                  type="button"
                  onClick={() => {
                    roleNavigate('/upload-image-flow-step-two');
                  }}
                  onTouchStart={() => {
                    roleNavigate('/upload-image-flow-step-two');
                  }}
                  style={{ width: '245px', height: '46px', cursor: 'pointer', touchAction: 'manipulation' }}
                  className={`${images !== null ? styles.nextBtn : styles.skipBtn}`}
                >
                  {images !== null ? 'Next' : 'Skip'}
                </Button>
                <Button variant="text" className={styles.medicalHistoryFooterBack} onClick={() => roleNavigate(-1)}>
                  <ArrowBackIosNewIcon />
                  Back
                </Button>
              </div>
            </div>
          </form>
          {openCamera && <Camera onClose={onCloseCamera} onPhotoTaken={onPhotoTaken} />}
        </div>
      </div>
      {showAddDependentModal && (
        <AddDependentModal isOpen={showAddDependentModal} setIsOpen={setShowAddDependentModal} isFromSelectNeeds />
      )}
    </Container>
  );
};
export default ImageUploadStepOne;
